import { faCopy, faEllipsisV } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { StatusCode } from "api/protocols";
import { GetNewsLetterPosts, GetNewsLetterSubscribers, PostDuplicateNewsLetterPost, PutExportNewsletterSubscribers, TNewsLetterPost, TNewsLetterSubscriber } from "api/rpc/2024-04/facilityAdmin/facility/newsletter";
import axios, { CancelToken } from "axios";
import { Badge } from "components/badge/Badge";
import { ButtonNew } from "components/buttonNew";
import Card from "components/card/Card";
import DataTable from "components/dataTable/DataTable";
import Input from "components/form/input";
import MessageDisplay from "components/messageDisplay/MessageDisplay";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import Page from "components/page/Page";
import Tabs from "components/tabs/Tabs";
import { downloadCsv, valueToString } from "helpers/Helpers";
import useDebounce from "hooks/events/useDebounce";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { showError, showSuccess } from "redux/actions/ui";

const NEWSLETTER_POST_LIMIT = 25;

enum SelectedTab {
    Posts = 0,
    Subscribers = 1,
}

export default function NewsletterPosts() {
    const {t} = useTranslation();
    const dispatch = useAppDispatch();
    const [newsLetterPosts, setNewsLetterPosts] = useState<Array<TNewsLetterPost>>(undefined);
    const [newsLetterSubscribers, setNewsLetterSubscribers] = useState<Array<TNewsLetterSubscriber>>(undefined);
    const [offset, setOffset] = useState<number>(0);
    const [subscriberOffset, setSubscriberOffset] = useState<number>(0);
    const history = useHistory();
    const [selectedTab, setSelectedTab] = useState<SelectedTab>(SelectedTab.Posts);
    const {facilityStore} = useAppSelector(store => store);
    const [postSearch, setPostSearch] = useState("");
    const debouncedPostSearch = useDebounce(
        postSearch,
        500,
      );
    const [subscriberSearch, setSubscriberSearch] = useState("");
    const debouncedSubscriberSearch = useDebounce(
        subscriberSearch,
        500,
      );


    useEffect(() => {
        const source = axios.CancelToken.source();
        void getNewsLetterPosts(source.token);
        return () => source.cancel();
    }, [offset, debouncedPostSearch]);

    useEffect(() => {
        const source = axios.CancelToken.source();
        void getNewsLetterSubscribers(source.token);
        return () => source.cancel();
    }, [subscriberOffset, debouncedSubscriberSearch]);

    async function getNewsLetterPosts(cancelToken?: CancelToken) {
        //loader
        if (newsLetterPosts !== undefined) {
            setNewsLetterPosts(undefined);
        }
        const res = await GetNewsLetterPosts({limit: NEWSLETTER_POST_LIMIT, offset: offset, search: debouncedPostSearch ? debouncedPostSearch : undefined}, false, cancelToken);
        if (res?.status !== StatusCode.OK) {
            setNewsLetterPosts([]);
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error getting newsletter posts")));
            return;
        }
        setNewsLetterPosts(res?.data);
    }

    async function duplicateNewsletterPost(id: number) {
        const res = await PostDuplicateNewsLetterPost({id: id}, true);
        if (res?.status !== StatusCode.OK) {
            dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error duplicating newsletter post")));
            return;
        }
        dispatch(showSuccess(t("Successfully duplicated newsletter post")));
        await getNewsLetterPosts();
    }

    async function getNewsLetterSubscribers(cancelToken: CancelToken) {
        //loader
        if (newsLetterSubscribers !== undefined) {
            setNewsLetterSubscribers(undefined);
        }
        const res = await GetNewsLetterSubscribers({limit: NEWSLETTER_POST_LIMIT, offset: subscriberOffset, search: debouncedSubscriberSearch ? debouncedSubscriberSearch : undefined}, false, cancelToken);
        if (res?.status !== StatusCode.OK) {
            setNewsLetterSubscribers([]);
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error getting newsletter subscribers")));
            return;
        }
        setNewsLetterSubscribers(res?.data);
    }

    async function copyLivePostLink(e: React.MouseEvent, postId: number) {
        e.stopPropagation();
        const link = window.origin + `/portal/golfnorth/newsletter/${facilityStore?.facility?.short_name}?newsletter_id=${postId}`;
        await navigator.clipboard.writeText(link);
        dispatch(showSuccess(t("Copied link to clipboard")));
    }

    async function exportSubscribers() {
        const res = await PutExportNewsletterSubscribers(true);
        if (res?.status !== StatusCode.OK) {
          dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error exporting subscribers")));
          return;
        }
        void downloadCsv(res.data as string, `NEWSLETTER_SUBSCRIBERS_${moment().format("YYYY-MM-DD").toString()}`
          .replace(/ /g, "_")
          .toLowerCase() + '.csv');
      }

    return (
    <Page
    title={t("Newsletter")}
    narrow
    subtitle={"View and manage newsletter posts and keep track of your subscribers in one place"}
    >
        <Tabs tabs={[{content: t("Posts")}, {content: t("Subscribers")}]} selected={selectedTab} onSelect={tab => setSelectedTab(tab)} variant="underline"/>
        <br />
        {selectedTab === SelectedTab.Posts ? (
            <Card title={t("Newsletter Posts")} subtitle={t("Create, draft and publish your newsletter posts here")} titleActions={[{content: t("New Post"), action: () => history.push("/admin/newsletter/new"), buttonType: "primary"}]}
            titleDetails={[{content: <Input labelHidden placeholder={t("Search posts...")} onSearchClear={() => setPostSearch("")} value={postSearch} onChange={e => setPostSearch(e.target.value)}/>}]}
            >
                <Card.Section>
            <DataTable 
            columns={[{label: t("Title")}, {label: t("Status")}, {label: t("Post Link")}, {label: t("Actions")}]} 
            loading={newsLetterPosts === undefined} footer={{
                tableLimit: NEWSLETTER_POST_LIMIT,
                tableOffset: offset,
                handleTableOffset: direction => direction === "next" ? setOffset(offset + NEWSLETTER_POST_LIMIT) : setOffset(offset - NEWSLETTER_POST_LIMIT),
                disableNextOffset: !(newsLetterPosts?.length === NEWSLETTER_POST_LIMIT) || newsLetterPosts === undefined,
            }}
            >
                {newsLetterPosts && newsLetterPosts?.length === 0 ? (
                    <tr>
                        <td colSpan={4}>
                            <MessageDisplay icon={"magnifying-glass"} title={t("No newsletter posts found")} content={t("Create a new newsletter post to populate this list")}/> 
                        </td>
                    </tr>
                ) : newsLetterPosts?.map((post, index) => {
                    return (
                        <tr key={index} className="clickable" onClick={() => history.push(`/admin/newsletter/${post?.id}`)}>
                            <td style={{maxWidth: "100px", overflow: "hidden", textOverflow: "ellipsis"}}>{post?.title}</td>
                            <td><Badge size="medium" type={post?.status === "draft" ? "warning" : post?.status === "published" ? "success" : "gray"}>{valueToString(post?.status)}</Badge></td>
                            {post?.status === "published" ? (
                                 <td onClick={e => copyLivePostLink(e, post?.id)}><p className="text-semibold" style={{fontSize: "14px"}}>{t("Copy")} <FontAwesomeIcon icon={faCopy}/></p></td>
                            ) : <td></td>}
                            <td> <NavigationDropdownNew
                                showPlainTextLabel
                                rightAlign
                                label={
                                <div>
                                    <ButtonNew style={{ padding: 10 }} size="medium" type="secondary">
                                    <FontAwesomeIcon icon={faEllipsisV} />
                                    </ButtonNew>
                                </div>
                                }
                                sections={[
                                [
                                    {
                                    label: t("Edit Post"),
                                    icon: "pen",
                                    onClick: () => history.push(`/admin/newsletter/${post?.id}`),
                                    },
                                    {
                                    label: t("Duplicate Post"),
                                    icon: "copy",
                                    onClick: () => duplicateNewsletterPost(post?.id),
                                    },
                                ],
                                ]}
                            /></td>
                        </tr>
                    );
                })}
            </DataTable>
            </Card.Section>
            </Card>
        ) : selectedTab === SelectedTab.Subscribers ? (
            <Card 
            title={t("Subscribers")} 
            subtitle={t("View and manage your newsletter subscribers here")} 
            titleActions={[{content: t("Export List"), action: () => exportSubscribers(), buttonType: "primary"}]}
            titleDetails={[{content: <Input labelHidden placeholder={t("Search subscribers...")} onSearchClear={() => setSubscriberSearch("")} value={subscriberSearch} onChange={e => setSubscriberSearch(e.target.value)}/>}]}
            >
                <Card.Section>
            <DataTable 
            columns={[{label: t("Recipient")}, {label: t("Email")}, {label: t("Customer Type")}, {label: t("Status")}]} 
            loading={newsLetterSubscribers === undefined} footer={{
                tableLimit: NEWSLETTER_POST_LIMIT,
                tableOffset: subscriberOffset,
                handleTableOffset: direction => direction === "next" ? setSubscriberOffset(subscriberOffset + NEWSLETTER_POST_LIMIT) : setSubscriberOffset(subscriberOffset - NEWSLETTER_POST_LIMIT),
                disableNextOffset: !(newsLetterSubscribers?.length === NEWSLETTER_POST_LIMIT) || newsLetterSubscribers === undefined,
            }}
            >
                {newsLetterSubscribers && newsLetterSubscribers?.length === 0 ? (
                    <tr>
                        <td colSpan={4}>
                            <MessageDisplay icon={"magnifying-glass"} title={t("No subscribers yet")} content={t("Newsletter subscribers will appear here once a post is published and people sign up.")}/> 
                        </td>
                    </tr>
                ) : newsLetterSubscribers?.map((subscriber, index) => {
                    return (
                        <tr key={index}>
                            <td>{subscriber?.user_first_name} {subscriber?.user_last_name}</td>
                            <td>{subscriber?.user_email}</td>
                            <td>{subscriber?.customer_type_title} {subscriber?.customer_type_subtitle ? ` - ${subscriber?.customer_type_subtitle}` : null}</td>
                            <td><Badge size="medium" type={subscriber?.subscribed ? "success" : "error"}>{subscriber?.subscribed ? t("Subscribed") : t("Unsubscribed")}</Badge></td>
                        </tr>
                    );
                })}
            </DataTable>
            </Card.Section>
            </Card>
        ) : null}
        
    </Page>  
    );
}